import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { PlantsService } from '@cemex-core/cmx-api';
import {
  LOAD_JOBSITE_PLANT_FAILED,
  LOAD_SALES_AREA_SUCCESS,
  LoadJobsitePlantFail,
  LoadJobsiteSalesAreaComplete,
  SET_JOBSITE_PLANT,
  SetJobsitePlant,
} from '../../../actions/jobsite-actions/jobsite.actions';
import { catchError, filter, map, mergeMap, skip, switchMap, takeUntil } from 'rxjs/operators';
import { Plant } from '@cemex-core/cmx-api/models/order-management/plant';
import { RenderMapAction } from '../../../actions/location-map-actions/location-map.actions';
import { MAP_ADDRESS_TYPES } from '../../../../../../shared/constants/map-address-types';
import { CmxMapConfig } from '../../../../../../shared/models/cmx-map-config/cmx-map-config';
import { DisableNavigation } from '../../../../../actions/stepper-actions';
import { CmxClientService } from '@cemex/cmx-client-layout';
import { TranslateService } from '@ngx-translate/core';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';

@Injectable()
export class JobsitePlantEffects {
  plantLoad$: Observable<Action | Action[]> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_SALES_AREA_SUCCESS),
      filter(() => this.isEffectAvailable()),
      switchMap((payload: LoadJobsiteSalesAreaComplete) => {
        const nextLoad$ = this.actions$.pipe(ofType(LOAD_SALES_AREA_SUCCESS), skip(1));

        if (!payload.payload[0] || !payload.payload[0].plant || !payload.payload[0].plant.plantId) {
          return of(new LoadJobsitePlantFail('Jobsite does not have a plant.'));
        }

        return this.plantsService.getPlant(payload.payload[0].plant.plantId).pipe(
          takeUntil(nextLoad$),
          mergeMap((plant: Plant) => {
            this.alerts.closeAllAlerts();
            return [new DisableNavigation(false), new SetJobsitePlant([plant])];
          }),
          catchError((error) => of(new LoadJobsitePlantFail(error))),
        );
      }),
    ),
  );

  loadPlantAddress$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_JOBSITE_PLANT),
      filter(() => this.isEffectAvailable()),
      map((payload: SetJobsitePlant) => {
        return new RenderMapAction(new CmxMapConfig(payload.payload[0].address, MAP_ADDRESS_TYPES.PLANT));
      }),
    ),
  );

  loadPlantFailed: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_JOBSITE_PLANT_FAILED),
      map(() => {
        this.alerts.addMainAlert('danger', this.translate.instant('views.location.no_default_plant'));
        return new DisableNavigation(true);
      }),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.JobsitePlantEffects);
  }

  constructor(
    private actions$: Actions,
    private plantsService: PlantsService,
    private alerts: CmxClientService,
    private translate: TranslateService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
